import React, { useMemo } from 'react';

import Box, { type BoxProps } from '@amzn/meridian/box/box';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import _sortBy from 'lodash/sortBy';
import { MeasureUnitAbbreviations, type MeasureUnitValues } from 'src/services/tasks/constants';
import { isNotEmpty } from 'src/utils/helpers';
import { type WeightRestrictionTask, type WeightRestrictionTaskGroup } from 'src/utils/restrictionUtils';

// **** Functions **** //

/**
 * Check that a child task has a `restrictionMaxValue` property.
 */
const filterChild = (child: WeightRestrictionTask) => {
  if (isNotEmpty(child.restrictionMaxValue)) {
    return true;
  } else {
    console.error(`Child restriction-task ${child.entityId} should not have an empty .restrictionMaxValue property`);
    return false;
  }
};

/**
 * Get a dropdown option
 */
const getOption = (task: WeightRestrictionTask): DropdownOption => ({
  label: task.restrictionMaxValue + '+ ' + MeasureUnitAbbreviations[task.restrictionMaxUnit ?? ''],
  value: task.restrictionMaxValue,
});

/**
 * If cannot perform this task
 */
const getFirstOption = (label: string, restrictionMaxUnit: MeasureUnitValues): DropdownOption => ({
  label: `0+ ${MeasureUnitAbbreviations[restrictionMaxUnit ?? '']} (Associate cannot ${label.toLowerCase()})`,
  value: 0,
});

// **** Types **** //

interface WeightRestrictionMaxRangeSelectorProps extends BoxProps {
  restrictionLabel: string;
  restrictionMaxUnit: MeasureUnitValues;
  restrictionChildTasks: WeightRestrictionTaskGroup['restrictionTasks'];
  selectedValue: number | undefined; // maxWeightRange
  onSelectValue: (value: number) => void;
  errorMessage?: string;
  isDisabled: boolean;
  /** @testOnly */
  index: number;
}

interface DropdownOption {
  label: string;
  value: number;
}

// **** Components **** //

/**
 * Select menu for the different weight options.
 */
const MaxRangeSelector = ({
  restrictionLabel,
  restrictionMaxUnit,
  restrictionChildTasks,
  selectedValue,
  onSelectValue,
  isDisabled,
  errorMessage,
  index,
  ...boxProps
}: WeightRestrictionMaxRangeSelectorProps) => {
  // Configure the list of dropdown options to display when 'maxRange' is changed
  const dropdownOptions = useMemo(() => {
    const firstOption = getFirstOption(restrictionLabel, restrictionMaxUnit);
    const children = _sortBy(restrictionChildTasks, 'restrictionMaxValue');
    const options = children.filter(filterChild).map(getOption);
    return [firstOption, ...options];
  }, [restrictionChildTasks, restrictionLabel, restrictionMaxUnit]);

  return (
    <Box data-testid="new-jmr__weight-restriction__max-range-selector" {...boxProps}>
      <Text>
        Associate cannot perform: <b>{restrictionLabel}</b> exceeding ({MeasureUnitAbbreviations[restrictionMaxUnit]}) *
      </Text>
      <Select
        aria-label={`${restrictionLabel} maximum range`}
        data-cy={`new-jmr__weight__restriction__weight-range__dropdown__${index}`}
        placeholder="Select..."
        value={selectedValue}
        onChange={onSelectValue}
        disabled={isDisabled}
        errorMessage={errorMessage}
        error={!!errorMessage}
      >
        {dropdownOptions.map((o) => (
          <SelectOption
            data-cy="new-jmr__weight-restrictions__dropdown-item"
            key={o.value}
            label={o.label}
            value={o.value}
          />
        ))}
      </Select>
    </Box>
  );
};

// **** Export **** //

export default MaxRangeSelector;
